import React from 'react';
import ReactDOM from 'react-dom';
import type { Editor } from '@tiptap/react';
import { TextSelection, type SelectionBookmark } from '@tiptap/pm/state';
import { CellSelection, findTable } from '@tiptap/pm/tables';
import { Divider, IconButton, Menu, MenuItem, Fade } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { getLiveEditor } from '@/shared/utils/tiptapEditor';

interface TableContextMenuProps {
  editor: Editor | null;
}

/** Viewport-fixed position for the floating button */
interface FloatingButtonState {
  top: number;
  left: number;
  table: HTMLTableElement;
  cell: HTMLTableCellElement | null;
}

const TableContextMenu: React.FC<TableContextMenuProps> = ({ editor }) => {
  const [buttonPos, setButtonPos] = React.useState<FloatingButtonState | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const isHoveringButtonRef = React.useRef(false);
  const preservedSelectionRef = React.useRef<SelectionBookmark | null>(null);
  const liveEditor = getLiveEditor(editor);

  const cancelHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const startHideTimer = () => {
    cancelHideTimer();
    hideTimerRef.current = setTimeout(() => {
      if (!anchorEl && !isHoveringButtonRef.current) setButtonPos(null);
    }, 500);
  };

  const getRenderedTableBounds = (table: HTMLTableElement) => {
    const cellRects = Array.from(table.querySelectorAll('td, th'))
      .map((cell) => cell.getBoundingClientRect())
      .filter((rect) => rect.width > 0 && rect.height > 0);

    if (cellRects.length === 0) {
      const rect = table.getBoundingClientRect();
      return { top: rect.top, left: rect.left, right: rect.right };
    }

    return {
      top: Math.min(...cellRects.map((rect) => rect.top)),
      left: Math.min(...cellRects.map((rect) => rect.left)),
      right: Math.max(...cellRects.map((rect) => rect.right)),
    };
  };

  // Anchor the floating control to the table's top-left corner so it stays
  // stable while the user moves from the table onto the button.
  const posFromTable = (
    table: HTMLTableElement,
    cell: HTMLTableCellElement | null
  ): FloatingButtonState => {
    const bounds = getRenderedTableBounds(table);
    const viewportPadding = 8;
    const buttonSize = 28;

    return {
      top: Math.max(
        viewportPadding,
        Math.min(bounds.top, window.innerHeight - buttonSize - viewportPadding)
      ),
      left: Math.max(
        viewportPadding,
        Math.min(bounds.left - buttonSize, window.innerWidth - buttonSize - viewportPadding)
      ),
      table,
      cell,
    };
  };

  const focusHoveredCell = (cell: HTMLTableCellElement | null, table: HTMLTableElement | null) => {
    const instance = getLiveEditor(editor);
    if (!instance) return;

    const targetCell = cell ?? table?.querySelector('td, th');
    if (!(targetCell instanceof HTMLTableCellElement)) return;

    const view = instance.view;
    const rect = targetCell.getBoundingClientRect();
    const posAtCoords = view.posAtCoords({
      left: rect.left + Math.min(rect.width / 2, 16),
      top: rect.top + Math.min(rect.height / 2, 16),
    });
    if (!posAtCoords) return;

    const { state } = view;
    view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, posAtCoords.pos)));
    view.focus();
  };

  const getSelectionTableElement = () => {
    const instance = getLiveEditor(editor);
    if (!instance) return null;

    const { selection } = instance.state;
    const tableInfo = findTable(
      selection instanceof CellSelection ? selection.$anchorCell : selection.$from
    );
    if (!tableInfo) return null;

    const tableDom = instance.view.nodeDOM(tableInfo.pos);
    if (tableDom instanceof HTMLTableElement) return tableDom;
    if (!(tableDom instanceof Element)) return null;
    if (tableDom.matches('table')) return tableDom as HTMLTableElement;

    return tableDom.querySelector('table');
  };

  const preserveSelectionIfInHoveredTable = (table: HTMLTableElement | null) => {
    const instance = getLiveEditor(editor);
    if (!instance || !table) {
      preservedSelectionRef.current = null;
      return false;
    }

    const selectionTable = getSelectionTableElement();
    if (selectionTable !== table) {
      preservedSelectionRef.current = null;
      return false;
    }

    preservedSelectionRef.current = instance.state.selection.getBookmark();
    return true;
  };

  const restorePreservedSelection = (instance: Editor) => {
    if (!preservedSelectionRef.current) return;

    const selection = preservedSelectionRef.current.resolve(instance.state.doc);
    if (!selection.eq(instance.state.selection)) {
      instance.view.dispatch(instance.state.tr.setSelection(selection));
    }
  };

  React.useEffect(() => {
    if (!liveEditor) return;

    let editorDom: HTMLElement;
    try {
      editorDom = liveEditor.view.dom;
    } catch {
      return;
    }
    if (!editorDom) return;

    const handleMouseMove = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const cell = target.closest('td, th') as HTMLTableCellElement | null;
      const table = cell?.closest('table') as HTMLTableElement | null;
      if (!table || !cell) {
        return;
      }

      cancelHideTimer();

      setButtonPos((prev) => {
        const next = posFromTable(table, cell);
        if (
          prev &&
          prev.table === table &&
          prev.cell === cell &&
          prev.top === next.top &&
          prev.left === next.left
        ) {
          return prev;
        }
        return next;
      });
    };

    const handleMouseLeave = (event: MouseEvent) => {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && buttonRef.current?.contains(relatedTarget)) {
        cancelHideTimer();
        return;
      }

      startHideTimer();
    };

    editorDom.addEventListener('mousemove', handleMouseMove);
    editorDom.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      editorDom.removeEventListener('mousemove', handleMouseMove);
      editorDom.removeEventListener('mouseleave', handleMouseLeave);
      cancelHideTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers close over stable refs; re-binding on every render would thrash listeners
  }, [liveEditor, anchorEl]);

  // Reposition on scroll so the button tracks the table
  React.useEffect(() => {
    if (!liveEditor || !buttonPos) return;
    let editorDom: HTMLElement;
    try {
      editorDom = liveEditor.view.dom;
    } catch {
      return;
    }
    const scrollParent = editorDom.closest('.document-editor-canvas') ?? editorDom.parentElement;
    if (!scrollParent) return;

    const reposition = () => {
      setButtonPos((prev) => {
        if (!prev?.table.isConnected) return null;
        return posFromTable(prev.table, prev.cell);
      });
    };

    scrollParent.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition, { passive: true });
    return () => {
      scrollParent.removeEventListener('scroll', reposition);
      window.removeEventListener('resize', reposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reposition reads buttonPos via functional setter; including posFromTable would re-register listeners on every position change
  }, [liveEditor, buttonPos]);

  const handleButtonMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    preserveSelectionIfInHoveredTable(buttonPos?.table ?? null);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    const didPreserveSelection = preserveSelectionIfInHoveredTable(buttonPos?.table ?? null);
    if (!didPreserveSelection) {
      focusHoveredCell(buttonPos?.cell ?? null, buttonPos?.table ?? null);
    }
    setAnchorEl(event.currentTarget);
  };

  const handleButtonMouseEnter = () => {
    isHoveringButtonRef.current = true;
    cancelHideTimer();
  };

  const handleButtonMouseLeave = () => {
    isHoveringButtonRef.current = false;
    startHideTimer();
  };

  const closeMenu = () => {
    setAnchorEl(null);
    preservedSelectionRef.current = null;
  };

  const run = (command: (instance: Editor) => void) => {
    const instance = getLiveEditor(editor);
    if (!instance) {
      closeMenu();
      return;
    }

    restorePreservedSelection(instance);
    command(instance);
    closeMenu();
  };

  if (!liveEditor) return null;

  const isTable = liveEditor.isActive('table');
  const canMerge = liveEditor.can().chain().focus().mergeCells().run();
  const canSplit = liveEditor.can().chain().focus().splitCell().run();
  const hasDuplicateRow =
    typeof (liveEditor.commands as Record<string, unknown>).duplicateRow === 'function';
  const hasDuplicateCol =
    typeof (liveEditor.commands as Record<string, unknown>).duplicateColumn === 'function';

  const floatingButton = (
    <Fade in={buttonPos !== null}>
      <IconButton
        ref={buttonRef}
        size="small"
        onMouseDown={handleButtonMouseDown}
        onClick={handleButtonClick}
        onMouseEnter={handleButtonMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
        sx={{
          position: 'fixed',
          top: buttonPos?.top ?? -9999,
          left: buttonPos?.left ?? -9999,
          zIndex: 1300,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          opacity: 0.7,
          width: 28,
          height: 28,
          borderRadius: 0,
          transition: 'opacity 0.15s',
          '&:hover': { opacity: 1, bgcolor: 'background.paper' },
          '@media print': { display: 'none' },
        }}
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Fade>
  );

  return (
    <>
      {ReactDOM.createPortal(floatingButton, document.body)}

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
        {hasDuplicateRow && (
          <MenuItem
            onClick={() => run((instance) => instance.chain().focus().duplicateRow().run())}
            disabled={!isTable}
          >
            Duplicate Row
          </MenuItem>
        )}
        {hasDuplicateCol && (
          <MenuItem
            onClick={() => run((instance) => instance.chain().focus().duplicateColumn().run())}
            disabled={!isTable}
          >
            Duplicate Column
          </MenuItem>
        )}
        {(hasDuplicateRow || hasDuplicateCol) && <Divider />}

        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().addColumnBefore().run())}
          disabled={!isTable}
        >
          Add Column Before
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().addColumnAfter().run())}
          disabled={!isTable}
        >
          Add Column After
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().deleteColumn().run())}
          disabled={!isTable}
        >
          Delete Column
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().addRowBefore().run())}
          disabled={!isTable}
        >
          Add Row Before
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().addRowAfter().run())}
          disabled={!isTable}
        >
          Add Row After
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().deleteRow().run())}
          disabled={!isTable}
        >
          Delete Row
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().mergeCells().run())}
          disabled={!canMerge}
        >
          Merge Cells
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().splitCell().run())}
          disabled={!canSplit}
        >
          Split Cell
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().toggleHeaderRow().run())}
          disabled={!isTable}
        >
          Toggle Header Row
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().toggleHeaderColumn().run())}
          disabled={!isTable}
        >
          Toggle Header Column
        </MenuItem>
        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().toggleHeaderCell().run())}
          disabled={!isTable}
        >
          Toggle Header Cell
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => run((instance) => instance.chain().focus().deleteTable().run())}
          disabled={!isTable}
        >
          Delete Table
        </MenuItem>
      </Menu>
    </>
  );
};

export default TableContextMenu;
